import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  AutoAwesome as ChatIcon,
  Send as SendIcon,
  DeleteOutline as ClearIcon,
  Mic as MicIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "./AddressbookPanelShell";
import useSpeechToText from "../../../components/common/useSpeechToText";
import { useWebLLM } from "../../../contexts/WebLLMContext";
import { apiGetComments, apiCreateComment } from "../../../graphQL/comment";
import { apiGetJournals, apiCreateJournal, apiUpdateJournal, apiUpsertJournal, apiCancelJournalReminder } from "../../../graphQL/journal";
import { apiUpdateContact } from "../../../graphQL/addressbook/collections/contacts";

/* ── Context assembly ── */

function extractContactContext(contact) {
  if (!contact) return "";
  const identity = contact.identity || {};
  const reach = contact.reach || {};
  const person = identity.person || {};
  const company = identity.company || {};

  const lines = ["CONTACT DETAILS:"];
  const name = [person.firstName, person.lastName].filter(Boolean).join(" ");
  if (name) lines.push(`Name: ${name}`);
  lines.push(`Nickname: ${person.nickname || "(none)"}`);
  if (person.title || person.role) lines.push(`Role: ${person.title || person.role}`);
  if (company.name) lines.push(`Company: ${company.name}`);
  if (company.type) lines.push(`Company role: ${company.type}`);
  if (contact.status) lines.push(`Status: ${contact.status}`);
  if (contact.category) lines.push(`Category: ${contact.category}`);

  const emails = Array.isArray(reach.emails) ? reach.emails : [];
  emails.forEach((e) => { if (e?.value?.trim()) lines.push(`Email: ${e.value}`); });

  const phones = Array.isArray(reach.phones) ? reach.phones : [];
  phones.forEach((p) => { if (p?.value?.trim()) lines.push(`Phone: ${p.value}`); });

  const social = Array.isArray(reach.social) ? reach.social : [];
  social.forEach((s) => { if (s?.value) lines.push(`${s.type || "Social"}: ${s.value}`); });

  const addresses = Array.isArray(reach.addresses) ? reach.addresses : [];
  addresses.forEach((a) => {
    const parts = [a.street, a.city, a.state, a.zip, a.country].filter(Boolean);
    if (parts.length) lines.push(`Address: ${parts.join(", ")}`);
  });

  const data = contact.data || {};
  const legacy = data.identityLegacy || {};
  const tags = Array.isArray(data.tags) ? data.tags.filter(Boolean) : (Array.isArray(contact.tags) ? contact.tags.filter(Boolean) : []);
  if (tags.length) lines.push(`Tags: ${tags.join(", ")}`);
  const notes = legacy.notes || identity.notes;
  const specialties = legacy.specialties || identity.specialties;
  const markets = legacy.markets || identity.markets;
  if (notes) lines.push(`Notes: ${notes}`);
  if (specialties) lines.push(`Specialties: ${specialties}`);
  if (markets) lines.push(`Markets: ${markets}`);

  return lines.join("\n");
}

function safeJson(value) {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
}

function extractJournalTextNotes(journal) {
  const jData = journal?.data ? (typeof journal.data === "string" ? JSON.parse(journal.data) : journal.data) : {};
  const notes = Array.isArray(jData.notes || jData.data?.notes) ? (jData.notes || jData.data?.notes) : [];
  return notes.filter((n) => {
    if (!n || typeof n !== "object") return false;
    if (!n.content || typeof n.content !== "string") return false;
    // Keep common typed notes and any untyped note with content.
    if (!n.type) return true;
    return n.type === "text" || n.type === "speech";
  });
}

function formatJournalNotes(notes) {
  if (!notes.length) return "";
  const lines = ["JOURNAL NOTES:"];
  notes.forEach((n, i) => {
    const date = n.createdAt ? new Date(n.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
    const prefix = date ? `[${date}]` : `[${i + 1}]`;
    lines.push(`${prefix} ${n.content || ""}`);
  });
  return lines.join("\n");
}

function formatComments(comments) {
  if (!comments.length) return "";
  const lines = ["COMMENTS:"];
  comments.forEach((c) => {
    const date = c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
    const text = c.data?.text || "";
    lines.push(`[${date}] ${text}`);
  });
  return lines.join("\n");
}

function formatReminders(reminders) {
  if (!reminders.length) return "";
  const lines = ["REMINDERS:"];
  reminders.forEach((r) => {
    const data = typeof r.data === "string" ? JSON.parse(r.data) : (r.data || {});
    const action = data.action || "";
    const details = data.details || "";
    const state = r.state || "";
    const due = r.dueDate ? new Date(r.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
    lines.push(`[${due}] ${action}${details ? ": " + details : ""} (${state})`);
  });
  return lines.join("\n");
}

const TOOL_DEFINITIONS = `
TOOLS:
You have access to tools to take actions on this contact. When the user asks you to create, update, or add something, respond with a tool call using this exact format on its own line:

[TOOL_CALL: tool_name({"param": "value"})]

Available tools:

1. create_reminder({"action": "call|email|text|schedule_meeting|meet_in_person", "amount": number, "unit": "minute|hour|day|week|month|year", "text": "optional details", "time": "HH:MM (optional, 24h format)"})
   - Creates a reminder for this contact. If the user specifies a time, include the "time" field (e.g. "14:30" in 24h format). If no time is specified, omit it (defaults to 8:00 AM for day or longer units). Use amount=0 when the user wants a reminder TODAY at a specific time (e.g. "remind me at 5pm" → amount=0, time="17:00").
   - CRITICAL: Only include "text" if the user explicitly provides details. NEVER invent or make up text. If the user says "remind me to call tomorrow at 8am", do NOT add any text field.
   - Example: [TOOL_CALL: create_reminder({"action": "call", "amount": 3, "unit": "day"})]
   - Example today at a time: [TOOL_CALL: create_reminder({"action": "call", "amount": 0, "unit": "day", "time": "17:00"})]
   - Example tomorrow at a time: [TOOL_CALL: create_reminder({"action": "call", "amount": 1, "unit": "day", "time": "14:30"})]
   - Example with user-provided details: [TOOL_CALL: create_reminder({"action": "call", "amount": 1, "unit": "day", "text": "Follow up on proposal"})]

2. add_comment({"text": "the comment text"})
   - Adds a comment to this contact. Example: [TOOL_CALL: add_comment({"text": "Had a great call, very interested in refinancing"})]

3. update_contact({"field": "field_path", "value": "new value"})
   - Updates a field on this contact. Valid fields: identity.person.firstName, identity.person.lastName, identity.person.nickname, identity.person.title, identity.person.role, identity.person.tagline, identity.company.name, data.notes, data.specialties, data.markets, status
   - Example: [TOOL_CALL: update_contact({"field": "identity.person.nickname", "value": "Rosie"})]

4. create_global_reminder({"action": "call|email|text|schedule_meeting|attend_meeting|meet_in_person", "amount": number, "unit": "minute|hour|day|week|month|year", "text": "optional details", "time": "HH:MM (optional, 24h format)"})
   - Creates a GLOBAL / personal reminder NOT tied to this contact. Use this when the user says any of the following (or similar):
     • "global reminder", "personal reminder", "self reminder", "reminder for me", "remind me (about X)"
     • "set a global", "add a global", "schedule a global", "make it global"
     • anything that explicitly says the reminder is for the user themselves and not about this contact
   - Same time semantics as create_reminder (amount/unit/time). Defaults to 8 AM when no time is given for day-or-longer units.
   - If the user says "remind me to call them tomorrow" they mean THIS contact — use create_reminder. If they say "remind me to take out the trash tomorrow" or "set a global reminder to call mom" they mean themselves — use create_global_reminder.
   - Example: [TOOL_CALL: create_global_reminder({"action": "call", "amount": 1, "unit": "day", "text": "Call mom", "time": "18:00"})]
   - Example without details: [TOOL_CALL: create_global_reminder({"action": "schedule_meeting", "amount": 2, "unit": "hour"})]

5. update_reminder({"dueDate": "ISO date string", "action": "call|email|text|...", "details": "reminder details text", "state": "done"})
   - Updates the currently selected reminder. Only available when a reminder is selected. Only include the fields you are actually changing — do NOT include fields the user did not ask to change.
   - CRITICAL: Do NOT include "dueDate" unless the user explicitly asks to change the date/time. Changing dueDate triggers a backend reschedule. If the user only asks to change details or action, do NOT send dueDate.
   - "details" is a description of what the reminder is about (e.g. "Follow up on proposal"). Only set "details" when the user explicitly says something like "add details", "change the details to", or "set the description to". Do NOT put your instructions or the user's command into the details field.
   - "state" can ONLY be set to "done" when the user EXPLICITLY says "mark as done", "mark it done", "set to done", "complete it", or "cancel the reminder". Do NOT set state to "done" for any other reason. Words like "do this", "to do", "doing" are NOT requests to mark done. If the user says "add details" or "update details", use the "details" field — NEVER set state to "done" in that case.
   - For dueDate (only when asked), use LOCAL time not UTC. For example, 5pm on April 23 2026 should be "2026-04-23T17:00:00" (no Z suffix, no timezone offset).
   - Example changing details only: [TOOL_CALL: update_reminder({"details": "Follow up on refinancing proposal"})]
   - Example changing date only: [TOOL_CALL: update_reminder({"dueDate": "2026-04-23T17:00:00"})]
   - Example marking as done: [TOOL_CALL: update_reminder({"state": "done"})]

IMPORTANT: Always include a brief text response before or after the tool call explaining what you did. Only use one tool call per response. If the user's request is ambiguous, ask for clarification instead of guessing.

CRITICAL RULES — DO NOT VIOLATE:
- ONLY output English text. NEVER output Chinese, Japanese, or any non-English characters.
- Tool calls must be EXACTLY the format shown: [TOOL_CALL: name({"key": "value"})] — nothing else inside the brackets.
- Do NOT add extra text, translations, timestamps, or annotations inside the [TOOL_CALL: ...] brackets.
- Do NOT hallucinate, invent, or fabricate any data. Only use information from the context provided above.
- If you are unsure about a value, ASK the user instead of guessing.
`;

function formatSelectedReminder(reminder) {
  if (!reminder) return "";
  const data = typeof reminder.data === "string" ? JSON.parse(reminder.data) : (reminder.data || {});
  const lines = ["SELECTED REMINDER (you can update this with update_reminder):"];
  lines.push(`ID: ${reminder.id}`);
  lines.push(`Action: ${data.action || "unknown"}`);
  lines.push(`Due: ${reminder.dueDate || "not set"}`);
  lines.push(`State: ${reminder.state || "created"}`);
  if (data.contactName) lines.push(`Contact: ${data.contactName}`);
  lines.push(`Details: ${data.text || "(none)"}`);
  return lines.join("\n");
}

function buildSystemPrompt(contactCtx, journalCtx, commentsCtx, remindersCtx, selectedReminderCtx) {
  const sections = [
    "You are a helpful assistant that answers questions about a specific person based on the context provided below.",
    "You can also take actions like creating reminders for this contact, creating global/personal reminders for the user themselves, adding comments, updating contact fields, and updating the selected reminder.",
    "Disambiguation between create_reminder (contact) and create_global_reminder (user/self): the default is create_reminder (about this contact). Switch to create_global_reminder ONLY when the user uses words like 'global', 'personal', 'self', 'for me', 'remind me to' followed by something that's clearly not about this contact, or otherwise makes it explicit the reminder is for themselves and not about the contact.",
    "STRICT RULE: Only use information from the context provided. NEVER make up, guess, or infer data that is not explicitly present in the context. If a field is empty, missing, or not mentioned in the context, say you don't have that information. Do not fabricate names, nicknames, dates, or any other details.",
    "Be concise and direct.",
    "",
    TOOL_DEFINITIONS,
    "",
    contactCtx,
  ];
  if (journalCtx) sections.push("", journalCtx);
  if (commentsCtx) sections.push("", commentsCtx);
  if (remindersCtx) sections.push("", remindersCtx);
  if (selectedReminderCtx) sections.push("", selectedReminderCtx);
  return sections.join("\n");
}

function buildContactContext(contact) {
  return [
    extractContactContext(contact),
    "",
    "FULL CONTACT JSON (authoritative):",
    safeJson(contact),
  ].join("\n");
}

/* ── GLOBAL / journal mode (contact-free) ──
   The standalone Journal page reuses THIS same ChatPanel with scope="global"
   and no contact. In that mode the context is built from the user's personal
   journal (notes + reminders) instead of a contact, and the tool-calls write
   back into that same global journal — ref { collection:"global", id:<authorId> },
   scope:"global", stamped SILENTLY with the active domain (never shown). */

const GLOBAL_TOOL_DEFINITIONS = `
TOOLS:
You can take actions on the user's personal journal. When the user asks you to add a note or set a reminder, respond with a tool call on its own line using this exact format:

[TOOL_CALL: tool_name({"param": "value"})]

Available tools:

1. add_note({"text": "the note text"})
   - Adds a note to the journal. ONLY include text the user actually provided — never invent content.
   - Example: [TOOL_CALL: add_note({"text": "Signed the new lease today"})]

2. create_reminder({"action": "call|email|text|schedule_meeting|meet_in_person|follow_up", "amount": number, "unit": "minute|hour|day|week|month|year", "text": "optional details", "time": "HH:MM (optional, 24h format)"})
   - Creates a personal reminder in the journal. If the user specifies a time, include "time" (e.g. "14:30"). Use amount=0 with a "time" when the user wants it TODAY at a specific time.
   - ONLY include "text" if the user explicitly provides details. NEVER invent text.
   - Example: [TOOL_CALL: create_reminder({"action": "follow_up", "amount": 2, "unit": "day", "text": "Review the budget"})]
   - Example today at a time: [TOOL_CALL: create_reminder({"action": "call", "amount": 0, "unit": "day", "time": "17:00"})]

IMPORTANT: Always include a brief text response explaining what you did. Only use one tool call per response. If the request is ambiguous, ask for clarification instead of guessing.

CRITICAL RULES — DO NOT VIOLATE:
- ONLY output English text. NEVER output non-English characters.
- Tool calls must be EXACTLY the format shown: [TOOL_CALL: name({"key": "value"})] — nothing else inside the brackets.
- Do NOT hallucinate, invent, or fabricate any data. Only use information from the context provided above.
- If you are unsure about a value, ASK the user instead of guessing.
`;

function buildGlobalSystemPrompt(journalCtx, remindersCtx) {
  const sections = [
    "You are a helpful assistant for the user's personal journal. Answer questions based on the journal context below.",
    "You can also take actions: add a note to the journal, or create a personal reminder.",
    "STRICT RULE: Only use information from the context provided. NEVER make up, guess, or infer data that is not explicitly present. If something is missing, say you don't have that information.",
    "Be concise and direct.",
    "",
    GLOBAL_TOOL_DEFINITIONS,
  ];
  if (journalCtx) sections.push("", journalCtx);
  if (remindersCtx) sections.push("", remindersCtx);
  return sections.join("\n");
}

function parseJournalNotesArray(journal) {
  if (!journal?.data) return [];
  const data = typeof journal.data === "string" ? (() => { try { return JSON.parse(journal.data); } catch { return {}; } })() : journal.data;
  const notes = data?.notes || data?.data?.notes || [];
  return Array.isArray(notes) ? notes : [];
}

let _chatNoteId = 0;
function makeChatNoteId() {
  return `note_${Date.now().toString(36)}_${(++_chatNoteId).toString(36)}`;
}

/* Stamp scope / domain (silent) / provenance onto a global journal write. */
function buildGlobalData(base, action, { scope, domain, provenanceKey, provenanceSource, authorId }) {
  const out = { ...base };
  if (scope) out.scope = scope;
  if (domain) out.domain = domain;
  if (provenanceKey && provenanceSource) {
    out[provenanceKey] = {
      source: provenanceSource,
      ...(scope ? { scope } : {}),
      updatedAt: new Date().toISOString(),
      action,
      authorId: String(authorId || ""),
    };
  }
  return out;
}

/* ── Tool call parser & executor ── */

const TOOL_CALL_REGEX = /\[TOOL_CALL:\s*(\w+)\((\{[\s\S]*?\})\)[^\]]*\]/;

function parseToolCall(text) {
  const match = text.match(TOOL_CALL_REGEX);
  if (!match) return null;
  try {
    const name = match[1];
    const args = JSON.parse(match[2]);
    return { name, args, raw: match[0] };
  } catch {
    return null;
  }
}

function stripToolCall(text) {
  return text.replace(TOOL_CALL_REGEX, "").trim();
}

function setTimeTo8am(date) {
  const d = new Date(date);
  d.setHours(8, 0, 0, 0);
  return d;
}

function addDuration(baseDate, amount, unit) {
  const d = new Date(baseDate);
  const n = Number(amount);
  if (!n || n <= 0) return d;
  if (unit === "minute") { d.setMinutes(d.getMinutes() + n); return d; }
  if (unit === "hour") { d.setHours(d.getHours() + n); return d; }
  if (unit === "day") { d.setDate(d.getDate() + n); return d; }
  if (unit === "week") { d.setDate(d.getDate() + n * 7); return d; }
  if (unit === "month") { d.setMonth(d.getMonth() + n); return d; }
  if (unit === "year") { d.setFullYear(d.getFullYear() + n); return d; }
  return d;
}

async function executeToolCall(tool, { contact, authorId, selectedReminder, activeDomain }) {
  const { name, args } = tool;
  const contactId = contact?.id;
  const domainArg = activeDomain ? { domain: activeDomain } : {};
  const person = contact?.identity?.person || {};
  const contactName = [person.firstName, person.lastName].filter(Boolean).join(" ");
  const contactEmail = contact?.reach?.emails?.[0]?.value || "";
  const contactPhone = contact?.reach?.phones?.[0]?.value || "";

  if (name === "create_reminder") {
    if (!contactId) return "Error: no contact is selected. Please select a contact first.";
    const unit = args.unit || "day";
    const isShortDuration = unit === "minute" || unit === "hour";
    const now = new Date();
    let dueDate;
    if (args.time) {
      // User specified a time like "17:00" — first try today at that time
      const [h, m] = args.time.split(":").map(Number);
      const todayAtTime = new Date(now);
      todayAtTime.setHours(h || 0, m || 0, 0, 0);

      if (!args.amount || args.amount === 0) {
        // No explicit offset — use today if still in the future, otherwise tomorrow
        if (todayAtTime > now) {
          dueDate = todayAtTime;
        } else {
          todayAtTime.setDate(todayAtTime.getDate() + 1);
          dueDate = todayAtTime;
        }
      } else {
        // Apply the offset, then set the requested time
        const rawDue = addDuration(now, args.amount, unit);
        dueDate = new Date(rawDue);
        dueDate.setHours(h || 0, m || 0, 0, 0);
      }
    } else if (isShortDuration) {
      dueDate = addDuration(now, args.amount || 1, unit);
    } else {
      dueDate = setTimeTo8am(addDuration(now, args.amount || 1, unit));
    }
    await apiCreateJournal({
      authorId,
      ref: { id: contactId, collection: "contacts" },
      type: "reminder",
      dueDate: dueDate.toISOString(),
      ...domainArg,
      data: {
        amount: args.amount || 1,
        unit: args.unit || "day",
        days: args.amount || 1,
        action: args.action || "call",
        text: args.text || "",
        contactId,
        contactName,
        contactEmail,
        contactPhone,
      },
    });
    return `Reminder created: ${args.action} in ${args.amount} ${args.unit}(s)`;
  }

  if (name === "create_global_reminder") {
    const unit = args.unit || "day";
    const isShortDuration = unit === "minute" || unit === "hour";
    const now = new Date();
    let dueDate;
    if (args.time) {
      const [h, m] = args.time.split(":").map(Number);
      const todayAtTime = new Date(now);
      todayAtTime.setHours(h || 0, m || 0, 0, 0);
      if (!args.amount || args.amount === 0) {
        if (todayAtTime > now) {
          dueDate = todayAtTime;
        } else {
          todayAtTime.setDate(todayAtTime.getDate() + 1);
          dueDate = todayAtTime;
        }
      } else {
        const rawDue = addDuration(now, args.amount, unit);
        dueDate = new Date(rawDue);
        dueDate.setHours(h || 0, m || 0, 0, 0);
      }
    } else if (isShortDuration) {
      dueDate = addDuration(now, args.amount || 1, unit);
    } else {
      dueDate = setTimeTo8am(addDuration(now, args.amount || 1, unit));
    }
    await apiCreateJournal({
      authorId,
      ref: { collection: "global", id: String(authorId) },
      type: "reminder",
      dueDate: dueDate.toISOString(),
      ...domainArg,
      data: {
        amount: args.amount || 1,
        unit: args.unit || "day",
        action: args.action || "call",
        text: args.text || "",
        url: "",
        scope: "global",
      },
    });
    return `Global reminder created: ${args.action} in ${args.amount || 1} ${args.unit || "day"}(s)`;
  }

  if (name === "add_comment") {
    if (!args.text) return "Error: comment text is required";
    await apiCreateComment({
      authorId,
      data: { text: args.text },
      ref: { collection: "contacts", id: contactId },
    });
    return `Comment added: "${args.text}"`;
  }

  if (name === "update_contact") {
    if (!args.field || args.value === undefined) return "Error: field and value are required";
    const VALID_FIELDS = [
      "identity.person.firstName", "identity.person.lastName", "identity.person.nickname",
      "identity.person.title", "identity.person.role", "identity.person.tagline",
      "identity.person.languages", "identity.company.name",
      "data.notes", "data.specialties", "data.markets", "status",
    ];
    if (!VALID_FIELDS.includes(args.field)) return `Error: invalid field "${args.field}"`;

    // Deep-set the field on a clone of the contact
    const clone = JSON.parse(JSON.stringify(contact));
    const keys = args.field.split(".");
    let cursor = clone;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!cursor[keys[i]] || typeof cursor[keys[i]] !== "object") cursor[keys[i]] = {};
      cursor = cursor[keys[i]];
    }
    cursor[keys[keys.length - 1]] = args.value;

    await apiUpdateContact({
      id: contactId,
      authorId,
      identity: clone.identity,
      reach: clone.reach,
      status: clone.status,
    });
    return `Updated ${args.field} to "${args.value}"`;
  }

  if (name === "update_reminder") {
    if (!selectedReminder) return "Error: no reminder is selected. Please select a reminder first.";

    // Handle "mark as done" — cancel the agenda job, then persist data.status = "done" to DB
    if (args.state === "done") {
      await apiCancelJournalReminder(selectedReminder.id);
      const existData = typeof selectedReminder.data === "string" ? JSON.parse(selectedReminder.data) : (selectedReminder.data || {});
      const doneData = { ...existData, status: "done" };
      const persisted = await apiUpdateJournal({ id: selectedReminder.id, data: doneData });
      const updatedReminder = persisted?.journal || { ...selectedReminder, data: doneData };
      return { message: "Reminder marked as done and agenda job cancelled.", updatedReminder };
    }

    // Accept "text" as alias for "details"
    const detailsValue = args.details !== undefined ? args.details : args.text;

    const updateInput = { id: selectedReminder.id };
    if (args.dueDate) {
      const raw = args.dueDate;
      const hasTimezone = /Z|[+-]\d{2}:\d{2}$/.test(raw);
      updateInput.dueDate = hasTimezone ? raw : new Date(raw).toISOString();
    }
    const existingData = typeof selectedReminder.data === "string"
      ? JSON.parse(selectedReminder.data) : (selectedReminder.data || {});
    const newData = { ...existingData };
    if (args.action) newData.action = args.action;
    if (detailsValue !== undefined) newData.text = detailsValue;
    updateInput.data = newData;

    const result = await apiUpdateJournal(updateInput);
    const parts = [];
    if (args.dueDate) parts.push(`due date to ${new Date(updateInput.dueDate).toLocaleString()}`);
    if (args.action) parts.push(`action to ${args.action}`);
    if (detailsValue !== undefined) parts.push(`details to "${detailsValue}"`);
    // Return the updated reminder so the caller can refresh state
    const updatedReminder = result?.journal || { ...selectedReminder, ...updateInput };
    return { message: `Reminder updated: ${parts.join(", ")}`, updatedReminder };
  }

  return `Unknown tool: ${name}`;
}

/* Global / journal-mode tool executor. Writes to the user's personal journal
   (ref { collection: refCollection, id: refId }) with scope + silent domain +
   provenance stamped on every write. Mirrors JournalPanel's own writes so the
   entries appear identically in the list. */
async function executeGlobalToolCall(tool, ctx) {
  const { name, args } = tool;
  const { authorId, activeDomain, refId, refCollection, journalRef } = ctx;
  const stampArgs = { ...ctx, domain: activeDomain };

  if (name === "add_note") {
    if (!args.text) return "Error: note text is required";
    const doc = journalRef.current;
    const existing = parseJournalNotesArray(doc);
    const note = { id: makeChatNoteId(), createdAt: Date.now(), authorId, type: "text", content: args.text };
    const notes = [note, ...existing];
    const input = {
      authorId,
      type: "journal",
      ref: { id: refId, collection: refCollection },
      data: buildGlobalData({ notes }, "chat-add-note", stampArgs),
    };
    if (activeDomain) input.domain = activeDomain;
    if (doc?.id) input.id = doc.id;
    const res = await apiUpsertJournal(input);
    journalRef.current = res?.journal || { ...(doc || {}), data: { notes } };
    return `Note added to journal: "${args.text}"`;
  }

  if (name === "create_reminder" || name === "create_global_reminder") {
    const unit = args.unit || "day";
    const isShortDuration = unit === "minute" || unit === "hour";
    const now = new Date();
    let dueDate;
    if (args.time) {
      const [h, m] = args.time.split(":").map(Number);
      const todayAtTime = new Date(now);
      todayAtTime.setHours(h || 0, m || 0, 0, 0);
      if (!args.amount || args.amount === 0) {
        if (todayAtTime > now) {
          dueDate = todayAtTime;
        } else {
          todayAtTime.setDate(todayAtTime.getDate() + 1);
          dueDate = todayAtTime;
        }
      } else {
        const rawDue = addDuration(now, args.amount, unit);
        dueDate = new Date(rawDue);
        dueDate.setHours(h || 0, m || 0, 0, 0);
      }
    } else if (isShortDuration) {
      dueDate = addDuration(now, args.amount || 1, unit);
    } else {
      dueDate = setTimeTo8am(addDuration(now, args.amount || 1, unit));
    }
    await apiCreateJournal({
      authorId,
      ref: { collection: refCollection, id: refId },
      type: "reminder",
      dueDate: dueDate.toISOString(),
      ...(activeDomain ? { domain: activeDomain } : {}),
      data: buildGlobalData({
        amount: args.amount || 1,
        unit: args.unit || "day",
        action: args.action || "follow_up",
        text: args.text || "",
        url: "",
      }, "chat-create-reminder", stampArgs),
    });
    return `Reminder created: ${args.action || "follow_up"} in ${args.amount || 1} ${args.unit || "day"}(s)`;
  }

  return `Unknown tool: ${name}`;
}

/* ── Message bubble ── */

const MessageBubble = ({ role, content, streaming }) => (
  <Box sx={{
    display: "flex",
    justifyContent: role === "user" ? "flex-end" : "flex-start",
    mb: 0.75,
  }}>
    <Box sx={{
      maxWidth: "85%",
      px: 1.5,
      py: 1,
      borderRadius: role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
      backgroundColor: role === "user" ? "#3B82F6" : "#F1F5F9",
      color: role === "user" ? "#fff" : "#1E293B",
    }}>
      <Typography sx={{ fontSize: 13, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
        {content}
        {streaming && <Box component="span" sx={{ animation: "blink 1s steps(2) infinite", "@keyframes blink": { "50%": { opacity: 0 } } }}> |</Box>}
      </Typography>
    </Box>
  </Box>
);

/* ── Main panel ── */

const NOOP = () => {};

/* Remove <think>…</think> reasoning blocks (DeepSeek-R1 style) for display.
   While a block is still open mid-stream, show a lightweight indicator. */
function stripThinkText(text) {
  if (!text) return text;
  const closed = text.replace(/<think>[\s\S]*?<\/think>/g, "").trim();
  const openIdx = closed.indexOf("<think>");
  if (openIdx !== -1) {
    const before = closed.slice(0, openIdx).trim();
    return before ? `${before}\n(thinking…)` : "(thinking…)";
  }
  return closed;
}

const ChatPanel = ({
  contact,
  authorId,
  onClose,
  onActionComplete,
  selectedReminder,
  // ── GLOBAL / journal mode (contact-free) ──
  // When scope === "global" the panel drops the contact and instead reads/writes
  // the user's personal journal. All optional; no-ops in the default contact mode.
  scope,
  domain,
  refId: refIdProp,
  refCollection = "contacts",
  provenanceKey,
  provenanceSource,
  // ── PLAIN / remote-LLM mode ──
  // `llm` swaps the in-browser WebLLM engine for an external one exposing the
  // same `.chat.completions.create()` streaming contract (e.g. the remote
  // Kimi/DeepSeek servers): { engine, ready, loading, error, statusText }.
  // `plain` skips contact/journal context and tool-calls entirely: a general
  // open-ended chat. All optional; no-ops in the existing modes.
  llm,
  plain,
  // ── CONTEXT mode ──
  // `contextText` supplies a ready-made context block (e.g. a campaign's
  // analytics summary). The chat answers strictly from it, with no tool calls.
  contextText,
  title = "Chat",
  titleExtras,
  emptyTitle,
  emptySubtitle,
  inputPlaceholder,
  maxTokens = 500,
  stripThink,
}) => {
  const isGlobal = scope === "global";
  const isExternal = !!llm;
  const webllm = useWebLLM();
  const engine = isExternal ? llm.engine : webllm.engine;
  const llmReady = isExternal ? !!llm.ready : webllm.ready;
  const llmLoading = isExternal ? !!llm.loading : webllm.loading;
  const llmError = isExternal ? llm.error : webllm.error;
  const { downloading, progress, init: initLLM, resetChat: resetWebLLMChat } = webllm;
  const resetChat = isExternal ? NOOP : resetWebLLMChat;

  // Start loading the in-browser LLM when the chat panel opens (not needed
  // when an external engine is supplied).
  useEffect(() => { if (!isExternal) initLLM(); }, [initLLM, isExternal]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [basePrompt, setBasePrompt] = useState("");
  const [tokenUsage, setTokenUsage] = useState(null);
  const listRef = useRef(null);
  const abortRef = useRef(false);

  // Voice-to-text dictation. Recognized speech is inserted into the chat
  // input for the user to review before sending (never auto-sent).
  const appendDictation = useCallback((finalText) => {
    setInput((prev) => {
      const trimmed = prev.trimEnd();
      return trimmed ? `${trimmed} ${finalText}` : finalText;
    });
  }, []);
  const {
    supported: speechSupported,
    isListening,
    interimTranscript,
    toggle: toggleDictation,
    stop: stopDictation,
  } = useSpeechToText({ onFinalText: appendDictation });

  const contactId = contact?.id;
  // Global-mode journal ref target (personal journal keyed on the user).
  const refId = isGlobal ? String(refIdProp || authorId || "") : undefined;
  // Holds the loaded global journal doc so add_note can append to its notes[].
  const globalJournalRef = useRef(null);

  // Active domain for SILENT journal isolation (never rendered). Prefer the
  // explicit `domain` prop (global mode passes the active domain) else fall back
  // to the signed-in user's domain in Redux. "All" resolves to "".
  const reduxDomain = useSelector((state) => state.auth.user?.domain || "");
  const activeDomain = (() => {
    const d = String(domain || reduxDomain || "").trim();
    return d && d !== "All" ? d : "";
  })();

  // Build final system prompt = base + selected reminder context
  const systemPrompt = selectedReminder
    ? basePrompt + "\n\n" + formatSelectedReminder(selectedReminder)
    : basePrompt;

  // Reset LLM context and clear messages when contact changes
  useEffect(() => {
    resetChat();
    setMessages([]);
  }, [contactId, contextText, resetChat]);

  // Load all context when contact changes
  useEffect(() => {
    // ── PLAIN mode: open-ended chat, no app context and no tools ──
    if (plain) {
      setBasePrompt("You are a helpful, knowledgeable assistant. Be concise, direct, and accurate. If you are unsure about something, say so.");
      return undefined;
    }

    // ── CONTEXT mode: answer only from the supplied context block ──
    if (contextText) {
      setBasePrompt(
        "You are a helpful assistant that answers questions about the data in the context below. " +
        "Be concise and direct. Give numbers exactly as they appear; compute simple percentages when asked. " +
        "STRICT RULE: only use information from the context. If something is not in the context, say you do not have it.\n\n" +
        contextText,
      );
      return undefined;
    }

    // ── GLOBAL / journal mode: context comes from the personal journal ──
    if (isGlobal) {
      if (!authorId || !refId) {
        setBasePrompt(buildGlobalSystemPrompt("", ""));
        return undefined;
      }
      let cancelled = false;
      setContextLoading(true);
      (async () => {
        try {
          const domainArg = activeDomain ? { domain: activeDomain } : {};
          const [journalRes, reminderRes] = await Promise.all([
            apiGetJournals({ authorId, refId, refCollection, type: "journal", ...domainArg }),
            apiGetJournals({ authorId, refId, refCollection, type: "reminder", ...domainArg }),
          ]);
          if (cancelled) return;
          const j = journalRes?.journals?.[0] || null;
          globalJournalRef.current = j;
          const textNotes = extractJournalTextNotes(j);
          const reminders = reminderRes?.journals || [];
          setBasePrompt(buildGlobalSystemPrompt(formatJournalNotes(textNotes), formatReminders(reminders)));
        } catch (err) {
          console.error("Failed to load journal chat context:", err);
          if (!cancelled) setBasePrompt(buildGlobalSystemPrompt("", ""));
        } finally {
          if (!cancelled) setContextLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }

    if (!contactId) {
      // No contact selected — set a generic prompt so chat still works
      setBasePrompt(buildSystemPrompt("No contact is currently selected. You can still answer general questions and help the user find or manage contacts.", "", "", ""));
      return;
    }

    let cancelled = false;
    setContextLoading(true);

    (async () => {
      try {
        const fetches = [
          apiGetComments({
            refCollection: "contacts",
            refId: contactId,
            ordering: { field: "createdAt", order: "Asc" },
            pagination: { page: 1, limit: 500 },
          }),
        ];

        if (authorId) {
          fetches.push(
            apiGetJournals({ authorId, refId: contactId, refCollection: "contacts", type: "journal", ...(activeDomain ? { domain: activeDomain } : {}) }),
            apiGetJournals({ authorId, refId: contactId, refCollection: "contacts", type: "reminder", ...(activeDomain ? { domain: activeDomain } : {}) }),
          );
        }

        const results = await Promise.all(fetches);
        if (cancelled) return;

        const commentsRes = results[0];
        const journalRes = authorId ? results[1] : null;
        const reminderRes = authorId ? results[2] : null;

        const contactCtx = buildContactContext(contact);

        const j = journalRes?.journals?.[0];
        const textNotes = extractJournalTextNotes(j);

        const reminders = reminderRes?.journals || [];
        const comments = commentsRes?.yrhComments || [];

        const prompt = buildSystemPrompt(
          contactCtx,
          formatJournalNotes(textNotes),
          formatComments(comments),
          formatReminders(reminders),
        );
        setBasePrompt(prompt);
      } catch (err) {
        console.error("Failed to load chat context:", err);
        setBasePrompt(buildSystemPrompt(buildContactContext(contact), "", "", ""));
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [plain, contextText, isGlobal, refId, refCollection, contactId, authorId, contact, activeDomain]);

  // Auto-scroll
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || !engine || !systemPrompt || generating) return;

    // Stop any in-progress dictation before sending.
    if (isListening) stopDictation();

    const userMsg = { role: "user", content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setGenerating(true);
    abortRef.current = false;

    const llmMessages = [
      { role: "system", content: systemPrompt },
      ...updatedMessages.map((m) => ({ role: m.role, content: m.content })),
    ];

    try {
      const chunks = await engine.chat.completions.create({
        messages: llmMessages,
        temperature: 0.3,
        top_p: 0.85,
        max_tokens: maxTokens,
        stream: true,
        stream_options: { include_usage: true },
      });

      let accumulated = "";
      let lastUsage = null;
      setMessages((prev) => [...prev, { role: "assistant", content: "", streaming: true }]);

      for await (const chunk of chunks) {
        if (abortRef.current) break;
        const delta = chunk.choices?.[0]?.delta?.content || "";
        if (delta) {
          accumulated += delta;
          const current = accumulated;
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = { role: "assistant", content: current, streaming: true };
            return copy;
          });
        }
        if (chunk.usage) lastUsage = chunk.usage;
      }
      if (lastUsage) setTokenUsage(lastUsage);

      const final = accumulated;

      // Check for tool calls in the LLM response (never in plain mode)
      const toolCall = plain || contextText ? null : parseToolCall(final);
      if (toolCall && authorId) {
        const displayText = stripToolCall(final).trim();
        // Show the text portion (if any) while executing the tool
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: "assistant",
            content: displayText || "Processing...",
            streaming: true,
          };
          return copy;
        });

        try {
          const result = isGlobal
            ? await executeGlobalToolCall(toolCall, {
                authorId, activeDomain, refId, refCollection,
                provenanceKey, provenanceSource, journalRef: globalJournalRef,
              })
            : await executeToolCall(toolCall, { contact, authorId, selectedReminder, activeDomain });
          // result can be a string or { message, updatedReminder }
          const resultMsg = typeof result === "string" ? result : result.message;
          const confirmMsg = `✓ ${resultMsg}`;
          setMessages((prev) => {
            const copy = [...prev];
            // Finalize assistant message
            copy[copy.length - 1] = {
              role: "assistant",
              content: displayText || confirmMsg,
            };
            // Add system confirmation as a separate message
            if (displayText) {
              copy.push({ role: "assistant", content: confirmMsg });
            }
            return copy;
          });
          // Notify parent so other panels (Reminders, Comments, etc.) can refresh
          // Pass updated reminder data if available
          const updatedReminder = typeof result === "object" ? result.updatedReminder : null;
          if (onActionComplete) onActionComplete(toolCall.name, updatedReminder);
        } catch (toolErr) {
          console.error("Tool execution failed:", toolErr);
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = {
              role: "assistant",
              content: displayText || "I tried to perform the action but it failed.",
            };
            copy.push({
              role: "assistant",
              content: `✗ Action failed: ${toolErr.message || "Unknown error"}`,
            });
            return copy;
          });
        }
      } else {
        // No tool call — just finalize the message
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: final };
          return copy;
        });
      }
    } catch (err) {
      console.error("Chat generation failed:", err);
      setMessages((prev) => [...prev.filter((m) => !m.streaming), { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setGenerating(false);
    }
  }, [input, engine, systemPrompt, messages, generating, contact, authorId, selectedReminder, onActionComplete, isListening, stopDictation, activeDomain, isGlobal, plain, contextText, maxTokens, refId, refCollection, provenanceKey, provenanceSource]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    abortRef.current = true;
    setMessages([]);
    setGenerating(false);
    resetChat();
    setTokenUsage(null);
  };

  const contactName = (() => {
    const person = contact?.identity?.person || {};
    const first = (person.firstName || "").trim();
    const last = (person.lastName || "").trim();
    return `${first} ${last}`.trim();
  })();

  const reminderLabel = (() => {
    if (!selectedReminder) return "";
    const data = typeof selectedReminder.data === "string"
      ? JSON.parse(selectedReminder.data) : (selectedReminder.data || {});
    const action = data.action || "reminder";
    const due = selectedReminder.dueDate
      ? new Date(selectedReminder.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
      : "";
    return `${action}${due ? " — " + due : ""}`;
  })();

  const canSend = input.trim() && llmReady && systemPrompt && !generating;

  const llmStatus = (() => {
    if (isExternal) {
      if (llm.error) return llm.error;
      if (!llmReady) return llm.statusText || "Connecting to AI server...";
      return null;
    }
    if (llmError) return "AI model failed to load. Check browser console.";
    if (downloading) return `Downloading AI model... ${Math.round(progress * 100)}%`;
    if (llmLoading) return "Loading AI model...";
    if (!llmReady) return "Waiting for AI model...";
    return null;
  })();

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.5 }}>
          <ChatIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>{title}</Typography>
          {!llmReady && !llmError && (
            <CircularProgress size={10} sx={{ ml: 0.5, color: "#7c3aed" }} />
          )}
          {titleExtras}
        </Box>
      }
      headerActions={
        messages.length > 0 ? (
          <IconButton size="small" onClick={handleClear} title="Clear chat" sx={{ color: "#9CA3AF", "&:hover": { color: "#6B7280" } }}>
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null
      }
      footerLeft={
        (contactName || reminderLabel) ? (
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {[contactName, reminderLabel].filter(Boolean).join(" · ")}
          </Typography>
        ) : null
      }
      footerRight={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {tokenUsage && (
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: tokenUsage.total_tokens > 24000 ? "#EF4444" : tokenUsage.total_tokens > 16000 ? "#F59E0B" : "#9CA3AF" }}>
              {tokenUsage.total_tokens.toLocaleString()} / 32K
            </Typography>
          )}
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {messages.length} entr{messages.length === 1 ? "y" : "ies"}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          {/* Messages */}
          <Box ref={listRef} sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
            {contextLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", py: 3 }}>
                <CircularProgress size={16} />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Loading context...</Typography>
              </Box>
            )}
            {!contextLoading && messages.length === 0 && (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <ChatIcon sx={{ fontSize: 32, color: "#E2E8F0", mb: 1 }} />
                <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.5 }}>
                  {emptyTitle
                    ? emptyTitle
                    : isGlobal
                    ? "Ask about your journal"
                    : contactName && selectedReminder
                      ? `Ask anything about ${contactName} or the selected reminder`
                      : contactName
                        ? `Ask anything about ${contactName}`
                        : selectedReminder
                          ? "Ask anything about the selected reminder"
                          : "Ask me anything"}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94A3B8", lineHeight: 1.5 }}>
                  {emptySubtitle
                    ? emptySubtitle
                    : isGlobal
                    ? "Your notes and reminders are included as context. I can add notes and set reminders."
                    : contactName
                      ? "Their details, journal notes, comments, and reminders are all included as context."
                      : selectedReminder
                        ? "You can update the due date, action, or details."
                        : "Select a contact or reminder to get started."}
                </Typography>
              </Box>
            )}
            {messages.map((msg, i) => (
              <MessageBubble
                key={i}
                role={msg.role}
                content={stripThink && msg.role === "assistant" ? stripThinkText(msg.content) : msg.content}
                streaming={msg.streaming}
              />
            ))}
          </Box>

          {/* Input */}
          <Box sx={{ p: 1, borderTop: "1px solid", borderColor: "divider", backgroundColor: "#FAFBFC", flexShrink: 0 }}>
            {llmStatus && (
              <Typography sx={{ fontSize: 11, color: llmError ? "#DC2626" : "#9CA3AF", mb: 0.5, textAlign: "center" }}>
                {llmStatus}
              </Typography>
            )}
            {isListening && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, flexWrap: "wrap" }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#3F51B5", animation: "pulse 1.5s infinite", "@keyframes pulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.3 } }, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12, color: "#3F51B5", fontWeight: 600, flex: 1, minWidth: 0 }}>
                  {interimTranscript || "Listening..."}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-end" }}>
              <Tooltip title={speechSupported ? (isListening ? "Stop dictation" : "Voice input") : "Voice input not supported in this browser"}>
                <span style={{ flexShrink: 0, alignSelf: "flex-end" }}>
                  <IconButton
                    size="small"
                    onClick={toggleDictation}
                    disabled={!speechSupported || !llmReady || contextLoading}
                    sx={{
                      color: isListening ? "#fff" : (speechSupported ? "#536DFE" : "#D1D5DB"),
                      backgroundColor: isListening ? "#3F51B5" : "transparent",
                      "&:hover": { backgroundColor: isListening ? "#3949AB" : "#E8EAF6", color: isListening ? "#fff" : "#3F51B5" },
                      "&.Mui-disabled": { color: "#D1D5DB", backgroundColor: "transparent" },
                      p: 0.5,
                    }}
                  >
                    {isListening ? <StopIcon sx={{ fontSize: 18 }} /> : <MicIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder={llmReady ? (inputPlaceholder || (contactName ? `Ask about ${contactName}${selectedReminder ? " or reminder" : ""}...` : selectedReminder ? "Ask about this reminder..." : "Ask me anything...")) : (isExternal ? "Waiting for AI server..." : "Waiting for AI model...")}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={!llmReady || contextLoading}
                size="small"
                sx={{ "& .MuiInputBase-root": { fontSize: 13, backgroundColor: "#fff" } }}
              />
              <IconButton
                size="small"
                onClick={handleSend}
                disabled={!canSend}
                sx={{
                  color: canSend ? "#7c3aed" : "#D1D5DB",
                  "&:hover": { color: "#6D28D9" },
                  p: 0.5,
                  flexShrink: 0,
                }}
              >
                {generating ? <CircularProgress size={18} sx={{ color: "#7c3aed" }} /> : <SendIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Box>
          </Box>
        </Box>
    </AddressbookPanelShell>
  );
};

export default ChatPanel;
